import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getProducts, submitOrder } from '../services/api';
import ConfirmBox from '../components/ConfirmBox';
import AlertBox from '../components/AlertBox';

const Shopping = () => {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [alertMessage , setAlertMessage] = useState(null);
    const [confirmAction, setConfirmAction]= useState(null);
    
    // Track quantities for each item variant
    const [quantities, setQuantities] = useState({
        chewToy: { 'TOY-Small': 0, 'TOY-Medium': 0, 'TOY-Large': 0 },
        premiumTreat: { 'PREMIUM TREAT-Small ear': 0, 'PREMIUM TREAT-Bully stick': 0 },
        packagedSmallTreats: { 'SMALL TREAT-Standard': 0 },
        hardPremiumChewBone: { 'HARD PREMIUM CHEW BONE-Small': 0, 'HARD PREMIUM CHEW BONE-Medium': 0, 'HARD PREMIUM CHEW BONE-Large': 0 },
        outsideWalk: { 'WALK-Standard': 0 },
        salonPamperPackage: { 'SALON-Standard': 0 }
    });

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }
    }, [navigate]);

    const updateQuantity = (category, sku, delta) => {
        setQuantities(prev => ({
            ...prev,
            [category]: {
                ...prev[category],
                [sku]: Math.max(0, (prev[category][sku] || 0) + delta)
            }
        }));
    };

    const getTotalQuantity = () => {
        let total = 0;
        Object.values(quantities).forEach(category => {
            Object.values(category).forEach(qty => {
                total += qty;
            });
        });
        return total;
    };

    const buildOrderItems = () => {
        const items = [];
        Object.values(quantities).forEach(category => {
            Object.entries(category).forEach(([sku, qty]) => {
                if (qty > 0) {
                    // Parse SKU format: "ProductId-SKU" (e.g., "TOY-Small")
                    const [productId, productSku] = sku.split('-');
                    items.push({
                        productId: productId,
                        sku: productSku,
                        quantity: qty
                    });
                }
            });
        });
        return items;
    };

    const handleSubmitOrder = async () => {
        setSubmitting(true);
        try {
            const orderItems = buildOrderItems();
            if(orderItems.length === 0){
                setAlertMessage("Please select at-least one item to order.");
            }else{
                setConfirmAction("SUBMIT_ORDER");
            }
        } catch (err) {
            setAlertMessage('Failed to submit order: ' + (err.message || 'Unknown error'));
        } finally {
            setSubmitting(false);
        }
    };

    const getConfirmActionMessage = () => {
        if(confirmAction == 'SUBMIT_ORDER'){
            return "Are you sure to submit your order?";
        }else
            return null;
    };

    const handleConfirmAction = async () => {
        setSubmitting(true);
        try{
            setConfirmAction(null);
            const orderItems = buildOrderItems();
            var resp = await submitOrder(orderItems);
            if(resp.status==200){
                // Reset quantities after successful order
                setQuantities({
                    chewToy: { 'TOY-Small': 0, 'TOY-Medium': 0, 'TOY-Large': 0 },
                    premiumTreat: { 'PREMIUM TREAT-Small ear': 0, 'PREMIUM TREAT-Bully stick': 0 },
                    packagedSmallTreats: { 'SMALL TREAT-Standard': 0 },
                    hardPremiumChewBone: { 'HARD PREMIUM CHEW BONE-Small': 0, 'HARD PREMIUM CHEW BONE-Medium': 0, 'HARD PREMIUM CHEW BONE-Large': 0 },
                    outsideWalk: { 'WALK-Standard': 0 },
                    salonPamperPackage: { 'SALON-Standard': 0 }
                });
                setAlertMessage("Order submitted successfully");
            }else{
                throw Error("Failed to submit order " + resp.status);
            }
        }catch(err){
            setAlertMessage(err);
        }finally{
            setSubmitting(false);
        }
        
    };

    // Quantity Control Component
    const QuantityControl = ({ category, sku, label, price, showBorder = true }) => {
        const qty = quantities[category][sku] || 0;
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 0',
                borderBottom: showBorder ? '1px solid #e0e0e0' : 'none'
            }}>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', color: '#000' }}>
                        {label} {price && <span style={{ color: '#666', fontSize: '12px' }}>(${price})</span>}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                        onClick={() => updateQuantity(category, sku, -1)}
                        disabled={qty === 0}
                        style={{
                            width: '32px',
                            height: '32px',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            background: qty === 0 ? '#f5f5f5' : '#ffffff',
                            cursor: qty === 0 ? 'not-allowed' : 'pointer',
                            color: qty === 0 ? '#ccc' : '#000',
                            fontSize: '18px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold'
                        }}
                    >
                        −
                    </button>
                    <span style={{ 
                        minWidth: '30px', 
                        textAlign: 'center', 
                        fontSize: '16px',
                        fontWeight: '500',
                        color: '#000'
                    }}>
                        {qty}
                    </span>
                    <button
                        onClick={() => updateQuantity(category, sku, 1)}
                        style={{
                            width: '32px',
                            height: '32px',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            background: '#ffffff',
                            cursor: 'pointer',
                            color: '#000',
                            fontSize: '18px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold'
                        }}
                    >
                        +
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div style={{ 
            width: '100%', 
            maxWidth: '480px', 
            margin: '0 auto',
            minHeight: '100vh',
            background: '#ffffff'
        }}>
            {/* Header */}
            <header style={{
                padding: '12px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#f0f0f0',
                borderBottom: '1px solid #e0e0e0'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img 
                        src="/dog_mascot.png" 
                        alt="Logo"
                        style={{ 
                            height: '32px',
                            width: 'auto',
                            objectFit: 'contain'
                        }}
                        onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'inline';
                        }}
                    />
                    <span style={{ fontSize: '24px', display: 'none' }}>🐕</span>
                    <h2 style={{ 
                        margin: 0, 
                        fontSize: '18px', 
                        fontWeight: 'bold', 
                        color: '#000',
                        fontFamily: 'Arial, sans-serif'
                    }}>
                        My Pup : Addons
                    </h2>
                </div>
                <button 
                    onClick={() => navigate('/video-stream')} 
                    style={{ 
                        background: 'transparent', 
                        border: 'none', 
                        color: '#AE3041',
                        fontSize: '20px',
                        cursor: 'pointer',
                        padding: '4px',
                        fontFamily: 'Arial, sans-serif'
                    }}
                >
                    ⬆️
                </button>
            </header>

            {/* Past Orders link */}
            <div style={{
                padding: '10px 16px',
                borderBottom: '1px solid #e0e0e0',
                textAlign: 'right'
            }}>
                <a
                    onClick={() => navigate('/past-orders')}
                    style={{
                        color: '#AE3041',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontFamily: 'Arial, sans-serif'
                    }}
                >
                    Past Orders
                </a>
            </div>

            <div style={{ padding: '16px' }}>
                <p style={{
                    marginBottom: '24px',
                    fontSize: '16px', 
                    color: '#000',
                    textAlign:"center",
                    fontFamily: 'Arial, sans-serif'
                }}>Order items for your pup.</p>
                
                {/**Chew toy */}
                <div style={{
                    padding: '12px 0',
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <div style={{ fontWeight: '500', marginBottom: '8px', color: '#000' }}>
                        Chew Toy
                    </div>
                    <QuantityControl category="chewToy" sku="TOY-Small" label="Small" price="9" />
                    <QuantityControl category="chewToy" sku="TOY-Medium" label="Medium" price="11" />
                    <QuantityControl category="chewToy" sku="TOY-Large" label="Large" price="19" showBorder={false} />
                </div>
                {/**Premium treat */}
                <div style={{
                    padding: '12px 0',
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <div style={{ fontWeight: '500', marginBottom: '8px', color: '#000' }}>
                        Premium Treat
                    </div>
                    <QuantityControl category="premiumTreat" sku="PREMIUM TREAT-Small ear" label="Small ear" price="4" />
                    <QuantityControl category="premiumTreat" sku="PREMIUM TREAT-Bully stick" label="Bully stick" price="11" showBorder={false} />
                </div>
                {/**Small treat */}
                <div style={{
                    padding: '12px 0',
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <div style={{ fontWeight: '500', marginBottom: '4px', color: '#000' }}>
                        Packaged small treats
                        <p style={{fontSize: '12px', fontWeight:'100'}}>
                            Will give few times a day
                        </p>
                    </div>
                    <QuantityControl category="packagedSmallTreats" sku="SMALL TREAT-Standard" label="Standard" price="8" showBorder={false} />
                </div>
                {/**Hard premium chew bone */}
                <div style={{
                    padding: '12px 0',
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <div style={{ fontWeight: '500', marginBottom: '8px', color: '#000' }}>
                        Hard premium chew bone
                    </div>
                    <QuantityControl category="hardPremiumChewBone" sku="HARD PREMIUM CHEW BONE-Small" label="Small" price="3" />
                    <QuantityControl category="hardPremiumChewBone" sku="HARD PREMIUM CHEW BONE-Medium" label="Medium" price="6" />
                    <QuantityControl category="hardPremiumChewBone" sku="HARD PREMIUM CHEW BONE-Large" label="Large" price="9" showBorder={false} />
                </div>
                {/**Outside walk */}
                <div style={{
                    padding: '12px 0',
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <div style={{ fontWeight: '500', marginBottom: '4px', color: '#000' }}>
                        Outside walk
                        <p style={{fontSize: '12px', fontWeight:'100'}}>
                            Approx. 20 mins, around neighborhood
                        </p>
                    </div>
                    <QuantityControl category="outsideWalk" sku="WALK-Standard" label="Standard" price="15" showBorder={false} />
                </div>
                {/**Salon pamper package */}
                <div style={{
                    padding: '12px 0',
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <div style={{ fontWeight: '500', marginBottom: '4px', color: '#000' }}>
                        Salon pamper package
                        <p style={{fontSize: '12px', fontWeight:'100'}}>
                            Includes bath, brush, nail trim, anal glad express and ear cleaning
                        </p>
                    </div>
                    <QuantityControl category="salonPamperPackage" sku="SALON-Standard" label="Standard" price="45" showBorder={false} />
                </div>
                
                {/* Total Quantity */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px 0',
                    marginTop: '16px',
                    borderTop: '2px solid #e0e0e0',
                    borderBottom: '2px solid #e0e0e0'
                }}>
                    <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#000' }}>
                        Total Quantity:
                    </div>
                    <div style={{ fontWeight: 'bold', fontSize: '18px', color: '#AE3041' }}>
                        {getTotalQuantity()}
                    </div>
                </div>
                
                <div className="flex-center" style={{ gap: '1rem', marginTop:'20px'}}>
                    <button
                        onClick={handleSubmitOrder}
                        className="btn btn-primary"
                        disabled={submitting}
                    >
                        {submitting ? 'Submitting...' : 'Submit'}
                    </button>
                </div>
                {/** Modals */}
                <AlertBox
                    show = {alertMessage != null}
                    title="Alert"
                    message={alertMessage}
                    onOK= {()=>setAlertMessage(null)}/>
                <ConfirmBox
                    isOpen={confirmAction !=null}
                    message={getConfirmActionMessage()}
                    onCancel={() => setConfirmAction(null)}
                    onConfirm={handleConfirmAction}
                />
            </div>
        </div>
);
};

export default Shopping;

